const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:50336';

const ACCESS_TOKEN_KEY = 'emlab_access_token';
const REFRESH_TOKEN_KEY = 'emlab_refresh_token';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export async function login(email: string, password: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/identity/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Login failed with status ${response.status}`);
  }

  const data: LoginResponse = await response.json();
  setAuthTokens(data.accessToken, data.refreshToken);
}

export function setAuthTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearAuthTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken());
}

export function getAuthHeaders(): HeadersInit {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export async function refreshTokens(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/api/identity/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearAuthTokens();
      return false;
    }

    const data: LoginResponse = await response.json();
    setAuthTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    clearAuthTokens();
    return false;
  }
}

// Centralized authorized fetch: attaches the bearer token, and on a 401 tries a
// single shared refresh, retrying the request once. If the refresh fails (e.g.
// the 5-day session expired), it clears tokens and redirects to the login page.
// Use this for EVERY admin/authorized request so expiry is handled consistently.
let refreshPromise: Promise<boolean> | null = null;

export async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const makeRequest = () =>
    fetch(url, { ...options, headers: { ...getAuthHeaders(), ...(options.headers ?? {}) } });

  let response = await makeRequest();
  if (response.status !== 401) return response;

  // Access token expired — refresh once (shared across concurrent callers).
  if (!refreshPromise) {
    refreshPromise = refreshTokens().finally(() => {
      refreshPromise = null;
    });
  }
  const refreshed = await refreshPromise;

  // Retry once if the refresh succeeded; a non-401 result means we recovered.
  if (refreshed) {
    response = await makeRequest();
    if (response.status !== 401) return response;
  }

  // Unrecoverable: either the refresh failed (session truly expired) or the
  // retry is still 401. Clear tokens and bounce to the login page so the user
  // never ends up stuck on a dead admin view spewing 401s.
  clearAuthTokens();
  if (!window.location.pathname.startsWith('/admin/login')) {
    window.location.href = '/admin/login';
  }
  return response;
}

export function getUserEmail(): string | null {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.email || decoded.sub || null;
  } catch {
    return null;
  }
}
